// ConfirmacionContext.tsx
import React, { createContext, useContext, useState, type ReactNode } from "react";
import { Modal, Text, Group, Button, Box } from "@mantine/core";
import { EnumTipoNotificacion } from "../interfaces";
import { IconCheck, IconExclamationMark, IconInfoCircle, IconX } from "@tabler/icons-react";

interface ConfirmacionOptions {
  titulo?: string;
  mensaje?: string;
  textoAceptar?: string;
  textoCancelar?: string;
  tipo?: EnumTipoNotificacion;
  opcionCerrarPopup?: boolean;
  opcionCancelar?: boolean;
}


interface ConfirmacionContextType {
  confirmar: (options: ConfirmacionOptions) => Promise<boolean>;
}

const obtenerIcono = (tipo: EnumTipoNotificacion) => {
  const estilos = {
    base: {
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      width: 32,
      height: 32,
      borderRadius: "50%",
    },
  };

  switch (tipo) {
    case EnumTipoNotificacion.Success:
      return (
        <Box style={{ ...estilos.base, backgroundColor: "#E6F6EA", color: "#2F9E44" }}>
          <IconCheck size={20} />
        </Box>
      );
    case EnumTipoNotificacion.Error:
      return (
        <Box style={{ ...estilos.base, backgroundColor: "#FDECEA", color: "#D32F2F" }}>
          <IconX size={20} />
        </Box>
      );
    case EnumTipoNotificacion.Warning:
      return (
        <Box style={{ ...estilos.base, backgroundColor: "#FFF4E5", color: "#ED6C02" }}>
          <IconExclamationMark size={20} />
        </Box>
      );
    case EnumTipoNotificacion.Information:
      return (
        <Box style={{ ...estilos.base, backgroundColor: "#E8F4FD", color: "#0288D1" }}>
          <IconInfoCircle size={20} />
        </Box>
      );
    default:
      return (
        <Box style={{ ...estilos.base, backgroundColor: "#E8F4FD", color: "#0288D1" }}>
          <IconInfoCircle size={20} />
        </Box>
      );
  }
};

// Función para obtener el color según el tipo
const obtenerColor = (tipo: EnumTipoNotificacion) => {
  switch (tipo) {
    case EnumTipoNotificacion.Success:
      return '#2CBA02';
    case EnumTipoNotificacion.Error:
      return '#BA0202';
    case EnumTipoNotificacion.Warning:
      return '#BA9202';
    case EnumTipoNotificacion.Information:
      return 'primary';
    default:
      return 'primary';
  }
};

const ConfirmacionContext = createContext<ConfirmacionContextType | undefined>(undefined);

export const useConfirmacion = (): ConfirmacionContextType => {
  const ctx = useContext(ConfirmacionContext);
  if (!ctx) throw new Error("useConfirmacion debe usarse dentro de ConfirmacionProvider");
  return ctx;
};

export const ProovedorConfirmacion: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [abierto, setAbierto] = useState(false);
  const [options, setOptions] = useState<ConfirmacionOptions>({});
  const [resolver, setResolver] = useState<(value: boolean) => void>();

  const confirmar = (opts: ConfirmacionOptions): Promise<boolean> => {
    setOptions(opts);
    setAbierto(true);
    return new Promise((resolve) => {
      setResolver(() => resolve);
    });
  };

  const manejarRespuesta = (valor: boolean) => {
    setAbierto(false);
    resolver?.(valor);
  };

  return (
    <ConfirmacionContext.Provider value={{ confirmar }}>
      {children}
      <Modal
      zIndex={100000000000}
      overlayProps={{
        backgroundOpacity: 0.55,
        blur: 1,
        zIndex: 100000000000,
      }}
        closeOnClickOutside={false}
        withCloseButton={options.opcionCerrarPopup === undefined || options.opcionCerrarPopup === true}
        title={
          <Group>
            {options.tipo && obtenerIcono(options.tipo)}
            <Text>{options.titulo ?? "Confirmación"}</Text>
          </Group>
        } opened={abierto} onClose={() => manejarRespuesta(false)} centered>
        <Text fz={'sm'} mb="lg">{options.mensaje ?? "¿Está seguro de continuar?"}</Text>
        <Group justify="flex-end">
          {options.opcionCancelar &&
            <Button radius='lg' variant="default" onClick={() => manejarRespuesta(false)}>
              {options.textoCancelar ?? "Cancelar"}
            </Button>
          }

          <Button radius='lg' color={options.tipo ? obtenerColor(options.tipo) : 'primary'} onClick={() => manejarRespuesta(true)}>
            {options.textoAceptar ?? "Aceptar"}
          </Button>
        </Group>
      </Modal>
    </ConfirmacionContext.Provider>
  );
};
